import { useState, useCallback, useEffect } from 'react'
import { MissingImage } from 'customizations'
import { Modal, withErrorBoundary, connected, Spinner, NoResults } from 'shared'
import { SmartButtonStub } from 'ui/smart-buttons'
import EventCardBadge from 'ui/events/EventCardBadge'
import { eventDateTime, eventDateTitle } from 'ui/events/EventHelper'

const CheckInModal = ({
  person,
  events,
  conditionalRoleCheckins,
  checkinMode,
  successMessage: initialSuccess,
  errorMessage: initialError,
  placeId,
  placeCheckinId,
  modalCloseCallback = () => {}
}) => {
  const [state, setState] = useState({
    loading: false,
    selectedEvent: null,
    successMessage: initialSuccess || '',
    errorMessage: initialError || '',
    eventsSubmitted: false,
    reservationTypes: []
  })

  const [roleCounts, setRoleCounts] = useState(() => {
    if (!conditionalRoleCheckins || _.isEmpty(conditionalRoleCheckins)) return {}

    const base = _.mapValues(_.keyBy(conditionalRoleCheckins, 'id'), () => 0)

    if (conditionalRoleCheckins.length === 1) {
      const rc = conditionalRoleCheckins[0]
      const used = rc.used_checkins ?? 0
      const total = rc.total_checkins
      const unlimited = total == null
      const remaining = unlimited ? Infinity : total - used

      if (remaining > 0) base[rc.id] = 1
    }

    return base
  })

  const getKioskIdFromUrl = useCallback(() => {
    const hash = window.location.hash
    const match = hash.match(/kiosk\/(\d+)/)
    return match ? parseInt(match[1], 10) : null
  }, [])

  const kioskId = getKioskIdFromUrl()

  const updateCount = useCallback((id, value) => {
    setRoleCounts(prev => ({ ...prev, [id]: value }))
  }, [])

  const waitAndCloseModal = () => {
    setTimeout(() => modalCloseCallback({ success: true }), 5000)
  }

  const handleSubmit = useCallback(async () => {
    setState(prev => ({
      ...prev,
      loading: true,
      errorMessage: '',
      successMessage: ''
    }))

    try {
      if (checkinMode === 'events' && state.selectedEvent) {
        const payload = { 
          reservation_id: state.selectedEvent.reservation_id, 
          user_id: person.id,
          place_id: placeId,
          place_checkin_id: placeCheckinId,
          source: 'web'
        }
        const res = await Backend.post('/users/checkin_event', payload)

        setState(prev => ({
          ...prev,
          loading: false,
          selectedEvent: null,
          eventsSubmitted: res?.success ? true : false,
          reservationTypes: res.reservation_types || [],
          ...(res?.success
            ? { successMessage: res.message || 'Event Check In Submitted' }
            : { errorMessage: res.message || 'Event check-in failed' })
        }))

        if (res?.success) waitAndCloseModal()
      } else if (checkinMode === 'manual') {
        const roleCheckins = (conditionalRoleCheckins || [])
          .map(rc => {
            const count = Math.min(roleCounts[rc.id] || 0, rc.total_checkins ?? Infinity)
            return count > 0 ? { conditional_role_id: rc.conditional_user_role_id, count } : null
          })
          .filter(Boolean)

        if (!roleCheckins.length) {
          setState(prev => ({ ...prev, loading: false }))
          return
        }

        try {
          if (placeCheckinId) {
            const res = await Backend.put('/users/place_checkins', {
              role_checkins: roleCheckins,
              user_id: person.id,
              place_id: placeId,
              place_checkin_id: placeCheckinId,
              source: 'web'
            })

            if (res?.success) {
              setState(prev => ({
                ...prev,
                loading: false,
                successMessage: 'Community Check In Submitted ',
                placeCheckinId: null
              }))
              waitAndCloseModal()
            } else {
              setState(prev => ({
                ...prev,
                loading: false,
                errorMessage: res.errors.join(', ') || 'Community Check In Submission Failed'
              }))
            }
          } else {
            const res = await Backend.post('/smart_buttons/run/place-checkin', {
              role_checkins: roleCheckins,
              user_id: person.id,
              place_id: placeId,
              source: 'web'
            })

            if (!res.errors || res.errors.length === 0) {
              setState(prev => ({
                ...prev,
                loading: false,
                successMessage: 'Community Check In Submitted ',
                placeCheckinId: null
              }))
              waitAndCloseModal()
            } else {
              setState(prev => ({
                ...prev,
                loading: false,
                errorMessage: res.errors.join(', ') || 'Community Check In Submission Failed'
              }))
            }
          }
        } catch (e) {
          console.error('Role check-in error', e)
          setState(prev => ({
            ...prev,
            loading: false,
            errorMessage: 'Community Check In Submission Failed'
          }))
        }
      } else {
        setState(prev => ({ ...prev, loading: false }))
        waitAndCloseModal()
      }
    } catch (err) {
      console.error('Check-in error', err)
      setState(prev => ({
        ...prev,
        loading: false,
        errorMessage: 'Unexpected error Community Check In Submission'
      }))
    }
  }, [checkinMode, state.selectedEvent, roleCounts, conditionalRoleCheckins, placeId, placeCheckinId, person.id])

  useEffect(() => {
    setState(prev => ({ ...prev, eventsSubmitted: false, selectedEvent: null }))
  }, [person?.id, checkinMode])

  const renderConditionalRoles = () => {
    if (checkinMode !== 'manual') return null
    if (_.isEmpty(conditionalRoleCheckins)) return <NoResults>No Role Checkins</NoResults>

    return conditionalRoleCheckins.map((rc, index) => {
      const used = rc.used_checkins ?? 0
      const total = rc.total_checkins
      const unlimited = total == null
      const remaining = unlimited ? Infinity : total - used
      const count = roleCounts[rc.id.toString()] || 0

      return (
        <div
          className="row mb-2 role-item d-flex flex-column flex-sm-row align-items-center"
          key={rc.id || index}
        >
          <div className="col-12 col-sm-6 text-center text-sm-start mb-1 mb-sm-0">
            {rc.role_name} {unlimited ? '(Unlimited)' : `(${used}/${total})`}
          </div>

          <div className="col-12 col-sm-6 d-flex justify-content-center justify-content-sm-end align-items-center gap-2">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm rounded-circle counter-btn p-0"
              disabled={count <= 0}
              onClick={() => updateCount(rc.id, count - 1)}
            >
              -
            </button>
            <span className="counter px-2">{count}</span>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm rounded-circle counter-btn p-0"
              disabled={remaining <= 0 || count >= remaining}
              onClick={() => updateCount(rc.id, count + 1)}
            >
              +
            </button>
          </div>
        </div>
      )
    })
  }

  const renderEvents = () => {
    if (checkinMode !== 'events' || state.eventsSubmitted) return null
    if (!events?.length) {
        return (
          <div className="p-3 modal-header border-0">
            <div className="d-flex align-items-center justify-content-center w-100">
              <NoResults className="username text-center">
                There are currently no reservations on your account for today.
              </NoResults>
            </div>
          </div>
        );
      }

    return (
      <div className="event-cards-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
        {events.map((event) => {
          const isSelected = state.selectedEvent?.reservation_id === event.reservation_id;

          const start = new Date(event.open_datetime);
          const formattedTime = `${start.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          })} ${start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`;
          return (
            <div
              key={event.reservation_id}
              style={{
                minWidth: '320px',
                cursor: 'pointer',
                border: isSelected ? '2px solid #6f42c1' : '1px solid #ddd',
                borderRadius: '16px',
                transition: 'border 0.2s',
              }}
              onClick={() => setState(prev => ({ ...prev, selectedEvent: event }))}
            >
              <custom-card className="EventCard">
                <div className="card">
                  <div className="card-img-container">
                    <img
                      className="card-img-top"
                      src={event.image_preview_url || MissingImage}
                      alt={event.name}
                      style={{ height: '150px', objectFit: 'cover', width: '100%' }}
                    />
                  </div>

                  <div className="card-meta p-2">
                    <h5 className="card-heading">{event.name}</h5>
                    <div className="card-subheading">{event.place_name}</div>
                    <div className="card-subheading">{event.sub_title}</div>
                     <div
                      className="card-note"                      
                      style={{
                        textAlign: 'left'
                      }}
                    >
                      {formattedTime}
                    </div>
                    <EventCardBadge event={event} />
                  </div>
                </div>
              </custom-card>
            </div>
          );
        })}
      </div>
    )
  }

  const renderSubmitButton = () => {
    const hasSelection = state.selectedEvent || _.sum(_.values(roleCounts)) > 0
    if (!hasSelection) return null

    const buttonText = state.selectedEvent ? "Confirm Check In" : "Submit"
    return (
      <div className="col-12 footer text-right mt-3">
        <SmartButtonStub
          text={buttonText}
          buttonType="button"
          className="admin-purple inverted primary custom-font-size mb-1 mb-md-0"
          spinning={state.loading}
          click={handleSubmit}
        />
      </div>
    )
  }

  if (state.loading) return <Spinner />

  return (
    <div className="PersonModal CheckInModal custom-modal">
      <div className="p-3 modal-header">
        <div className="d-flex align-items-center">
          <div className="person-avatar-cell">
            <a className="plain-link" href={`#/people/${person?.id}`}>
              <img
                src={person?.picture || MissingImage}
                className="person-avatar rounded-circle"
                alt={person?.name || 'User'}
              />
            </a>
          </div>
          <div className="username">{person?.name}</div>
        </div>
        <Modal.CloseButton />
      </div>

      <div className="modal-body d-block">
        {state.successMessage && (
          <div className="alert alert-success" role="alert">
            {state.successMessage}
          </div>
        )}

        {state.errorMessage && (
          <div className="alert alert-danger" role="alert">
            {state.errorMessage}
          </div>
        )}

        {checkinMode === 'manual' && !(state.successMessage || state.errorMessage) && (
          <>
            <h4 className="section-info-heading mb-3">Community Passes:</h4>
            {renderConditionalRoles()}
          </>
        )}

        {checkinMode === 'events' && !state.eventsSubmitted && (
          <>
            <h4 className="section-info-heading mb-3">Your Reservations</h4>
            {renderEvents()}
          </>
        )}

        {checkinMode === 'events' && state.eventsSubmitted && state.reservationTypes.length > 0 && (
          <div className="reservation-summary mt-3 p-3 border rounded bg-light">
            <h5 className="mb-2" style={{ fontWeight: '600', fontSize: '16px' }}>Reservation Summary</h5>
            <div className="reservation-rows">
              {state.reservationTypes.map((r, i) => (
                <div 
                  key={i} 
                  className="d-flex justify-content-between align-items-center py-1"
                  style={{ borderBottom: i < state.reservationTypes.length - 1 ? '1px solid #ddd' : 'none' }}
                >
                  <span style={{ fontSize: '14px', color: '#333' }}>{r.type}</span>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#555' }}>{r.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {((checkinMode === "events") || !(state.successMessage || state.errorMessage)) && renderSubmitButton()}
      </div>
    </div>
  )
}

export default withErrorBoundary(connected(CheckInModal))